/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import Slider from '../../components/slider/Slider';
import useThemeContext from '../../theme/useThemeContext';
import {LayerSettingsMarks} from './LayerSettings';

export interface LayerOpacitySliderProps {
  className: string;
  headerClassName: string;
  label: string;
  value: number;
  onSlide: (event: any, value: number | number[]) => void;
}

const useStyles = makeStyles(() => ({
  // the callers' own slider wrappers are 95% wide for legacy - in the
  // current theme the row is already inset by the panel's padding, so the
  // slider uses the row's full width instead ...
  currentRoot : {
    width : '100% !important'
  }
}));

/**
 * Renders a map layer's labelled 0-100% opacity slider, shared by the
 * Geofence, Traffic, Weather and operational layer settings. The caller
 * supplies its own wrapper and header class names, which keeps each
 * layer's legacy theme layout; in the current theme the wrapper and the
 * slider stretch to the row's width.
 *
 * ```typescript
 *
 *   import {LayerOpacitySlider} from
 *     '@ion/dls/src/maps/layers/LayerOpacitySlider';
 *   ...
 *
 *   <LayerOpacitySlider className={styles.subLayersSlider}
 *     headerClassName={styles.subLayersHeader}
 *     label={translate('maps:geofence.opacity')}
 *     value={0.5}
 *     onSlide={onSlide} />
 *
 * ```
 *
 * @param   className       The wrapper's class name.
 * @param   headerClassName The label's class name.
 * @param   label           The slider's label.
 * @param   value           The current opacity, from `0` to `1`.
 * @param   onSlide         Invoked with the new opacity while sliding.
 * @returns                 The labelled opacity slider.
 */
export const LayerOpacitySlider = ({
  className,
  headerClassName,
  label,
  value,
  onSlide
}: LayerOpacitySliderProps) => {
  const classes     = useStyles();
  const {isCurrent} = useThemeContext();

  return (
    <div className={classnames(className, isCurrent && classes.currentRoot)}>
      <span className={headerClassName}>
        {label}
      </span>
      <Slider step={0.1}
        marks={LayerSettingsMarks}
        value={value}
        max={1}
        fullWidth={isCurrent}
        onSlide={onSlide} />
    </div>
  );
};

export default LayerOpacitySlider;
